import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { categories } from "@/mocks/categories";
import i18n from "@/i18n";

interface HeaderProps {
  transparent?: boolean;
}

export default function Header({ transparent = true }: HeaderProps) {
  const { t } = useTranslation();
  const { lang } = useParams<{ lang: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);

  const currentLang = lang || "pt";
  const isHome = location.pathname === `/${currentLang}/`;

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    i18n.changeLanguage(currentLang);
  }, [currentLang]);

  const navItems = [
    { key: "nav.home", href: `/${currentLang}/` },
    { key: "nav.spreadsheet", href: `/${currentLang}/cssbuy-spreadsheet/` },
    { key: "nav.guides", href: `/${currentLang}/#articles` },
    { key: "nav.reviews", href: `/${currentLang}/#review` },
    { key: "nav.shipping", href: `/${currentLang}/#shipping` },
    { key: "nav.qc", href: `/${currentLang}/#qc` },
    { key: "nav.safety", href: `/${currentLang}/#safety` },
  ];

  const languages = [
    { code: "pt", label: "PT-BR" },
    { code: "de", label: "DE" },
    { code: "en", label: "EN" },
  ];

  const getCategoryName = (cat: typeof categories[0]) => {
    if (currentLang === "pt") return cat.namePt;
    if (currentLang === "de") return cat.nameDe;
    return cat.name;
  };

  const switchLang = (code: string) => {
    const path = location.pathname.replace(/^\/(pt|de|en)/, `/${code}`);
    navigate(path);
    setLangOpen(false);
  };

  const scrollToSection = (href: string) => {
    if (href.includes("#")) {
      const id = href.split("#")[1];
      if (isHome) {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: "smooth" });
        }
      } else {
        navigate(href);
      }
    } else {
      navigate(href);
    }
    setMobileOpen(false);
  };

  // Header visibility: transparent state uses a subtle dark gradient so white text
  // always stands out against any hero image; scrolled state uses a solid light bg.
  const bgClass = scrolled || !transparent
    ? "bg-background-50/95 backdrop-blur-md shadow-sm border-b border-background-200/50"
    : "bg-gradient-to-b from-black/60 via-black/25 to-transparent";

  const textClass = scrolled || !transparent
    ? "text-foreground-900"
    : "text-white";

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${bgClass}`}
    >
      <div className="w-full px-4 md:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          <a
            href={`/${currentLang}/`}
            onClick={(e) => {
              e.preventDefault();
              navigate(`/${currentLang}/`);
            }}
            className={`font-heading text-xl md:text-2xl font-bold tracking-tight ${textClass} hover:opacity-80 transition-opacity whitespace-nowrap`}
          >
            CSSBuy<span className="text-accent-400">BR</span>
          </a>

          <nav className="hidden lg:flex items-center gap-5 xl:gap-7">
            {navItems.map((item) => (
              <a
                key={item.key}
                href={item.href}
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection(item.href);
                }}
                className={`text-sm font-semibold ${textClass} hover:text-accent-400 transition-colors whitespace-nowrap`}
              >
                {t(item.key)}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-2 md:gap-3">
            <a
              href={`/${currentLang}/article/cssbuy-buying-guide/`}
              onClick={(e) => {
                e.preventDefault();
                navigate(`/${currentLang}/article/cssbuy-buying-guide/`);
              }}
              className="hidden md:inline-flex items-center gap-2 px-4 py-2 bg-accent-500 text-white rounded-lg font-medium text-sm hover:bg-accent-600 transition-all whitespace-nowrap"
            >
              <i className="ri-shopping-cart-2-line"></i>
              {t("nav.shoppingGuide")}
            </a>

            <div className="relative">
              <button
                onClick={() => setLangOpen(!langOpen)}
                className={`flex items-center gap-1 text-sm font-semibold ${textClass} hover:text-accent-400 transition-colors px-2 py-1.5 rounded-lg border border-current/25`}
              >
                {currentLang === "pt" ? "PT-BR" : currentLang === "de" ? "DE" : "EN"}
                <i className="ri-arrow-down-s-line text-xs"></i>
              </button>
              {langOpen && (
                <div className="absolute right-0 mt-2 w-28 bg-background-50 rounded-lg shadow-lg border border-background-200/70 overflow-hidden z-50">
                  {languages.map((l) => (
                    <button
                      key={l.code}
                      onClick={() => switchLang(l.code)}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-background-100 transition-colors ${
                        currentLang === l.code ? "text-primary-600 font-medium bg-primary-50" : "text-foreground-700"
                      }`}
                    >
                      {l.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button
              className={`md:hidden p-2 ${textClass}`}
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Menu"
            >
              <i className={`ri-${mobileOpen ? "close" : "menu"}-line text-2xl`}></i>
            </button>
          </div>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/50 px-4 py-4 max-h-[75vh] overflow-y-auto">
          <a
            href={`/${currentLang}/article/cssbuy-buying-guide/`}
            onClick={(e) => {
              e.preventDefault();
              navigate(`/${currentLang}/article/cssbuy-buying-guide/`);
              setMobileOpen(false);
            }}
            className="flex items-center justify-center gap-2 px-4 py-3 bg-accent-500 text-white rounded-lg font-medium text-sm mb-3"
          >
            <i className="ri-shopping-cart-2-line"></i>
            {t("nav.shoppingGuide")}
          </a>

          <div className="space-y-1 mb-4">
            {navItems.map((item) => (
              <a
                key={item.key}
                href={item.href}
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection(item.href);
                }}
                className="flex items-center gap-2 px-4 py-3 text-sm font-medium text-foreground-700 hover:text-primary-600 hover:bg-background-100 rounded-lg transition-colors"
              >
                {t(item.key)}
              </a>
            ))}
          </div>

          <div className="border-t border-background-200/50 pt-4 mb-4">
            <p className="text-xs font-semibold text-foreground-500 uppercase tracking-wide mb-2 px-1">
              {t("categories.title")}
            </p>
            <div className="grid grid-cols-2 gap-2">
              {categories.map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-2 p-2 bg-background-50 border border-background-200/70 rounded-lg hover:border-accent-300 transition-colors"
                >
                  <img
                    src={cat.image}
                    alt={getCategoryName(cat)}
                    className="w-8 h-8 rounded-md object-cover flex-shrink-0"
                  />
                  <span className="text-xs font-medium text-foreground-700 leading-tight">
                    {getCategoryName(cat)}
                  </span>
                </a>
              ))}
            </div>
          </div>

          <div className="border-t border-background-200/50 pt-3 flex gap-2">
            {languages.map((l) => (
              <button
                key={l.code}
                onClick={() => {
                  switchLang(l.code);
                  setMobileOpen(false);
                }}
                className={`px-4 py-2 text-sm rounded-lg border transition-colors ${
                  currentLang === l.code
                    ? "border-primary-500 text-primary-600 bg-primary-50"
                    : "border-background-300 text-foreground-600 hover:border-primary-400"
                }`}
              >
                {l.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}